import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaqSection as FaqSectionType } from "../../../sanity/lib/types/faqSection";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(useGSAP);

export const FaqBlock = ({ data }: { data: FaqSectionType }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState<number>(-1);

    const toggleAccordion = useCallback(
        (index: number) => {
            if (activeIndex === index) {
                setActiveIndex(-1);
                closeAccordion(index);
            } else {
                setActiveIndex(index);
                closeAccordion(activeIndex);
                openAccordion(index);
            }
        },
        [activeIndex],
    );

    const openAccordion = useCallback((index: number) => {
        const activeElement = ref.current?.querySelector(`.faq-answer[data-index="${index}"]`);

        if (!activeElement) return;

        gsap.to(activeElement, {
            height: "auto",
            duration: 0.5,
        });
    }, []);

    const closeAccordion = useCallback((index: number) => {
        const activeElement = ref.current?.querySelector(`.faq-answer[data-index="${index}"]`);

        if (!activeElement) return;

        gsap.to(activeElement, {
            height: "0",
            duration: 0.5,
        });
    }, []);

    return (
        <section className={`container py-12`} ref={ref}>
            <div className="mx-2.5 md:mx-5">
                {data.title && <h2 className="mb-6 m-body-l md:d-body-l md:mb-6">{data.title}</h2>}
            </div>
            <div className="md:mx-5">
                <div>
                    {data.faqItems.map((faq, index) => (
                        <div key={index} className="border-b border-black">
                            <div className="px-2.5 md:px-5 border-b group border-b-gray-200">
                                <button
                                    onClick={() => toggleAccordion(index)}
                                    className="flex items-center justify-between w-full py-6 text-left text-black cursor-pointer m-button-small md:d-button-small"
                                >
                                    {faq.question}
                                    <span className="flex items-center justify-center ml-4 transition rounded-full h-7 w-7 group-open:rotate-45">
                                        <img
                                            className={`${activeIndex === index ? "hidden" : "block"} min-w-6 w-6 min-h-6 h-6 md:min-w-8 md:min-h-8`}
                                            src="/icon_plus.svg"
                                            alt="Plus Icon"
                                        />
                                        <img
                                            className={`${activeIndex === index ? "block" : "hidden"} min-w-6 w-6 min-h-6 h-6 md:min-w-8 md:min-h-8`}
                                            src="/icon_minus.svg"
                                            alt="Minus Icon"
                                        />
                                    </span>
                                </button>
                                <div data-index={index} className="h-0 overflow-hidden faq-answer">
                                    <p className="max-w-[90%] md:max-w-[670px] mt-2 mb-6 d-body-m text-black/70">
                                        {faq.answer}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
